import { ReactNode } from 'react';

import { DenialCheckAction } from 'models/DenialCheckAction';
import { HighCostMedsAction } from 'models/HighCostMedsAction';
import { SexOffenderCheckAction } from 'models/SexOffenderCheckAction';
import { SyncPatientToEhrAction } from 'models/SyncPatientToEhrAction';
import Workflow from 'models/Workflow';
import { WorkflowAction } from 'models/WorkflowAction';

import ConfirmReferralSource, { ConfirmedReferralSource, OutstandingSource } from '../../ConfirmReferralSource';
import { VALUE_NAMES } from '../../constants';
import DenialCheckReview, { DenialCheckSummary } from '../../DenialCheckReview';
import DuplicatePatientReview, { DuplicateLinkNote } from '../../DuplicatePatientReview';
import ExtractionResults from '../../ExtractionResults';
import HighCostMedsReview, { HighCostMedsSummary } from '../../HighCostMedsReview';
import ProcessAttachmentsNote from '../../ProcessAttachmentsNote';
import ReferralOutcomePanel, { ReferralOutcomeResult } from '../../ReferralOutcomePanel';
import SexOffenderCheckReview, { SexOffenderCheckSummary } from '../../SexOffenderCheckReview';
import SyncPatientBody from '../../syncPatient/SyncPatientBody';
import { ActionHandlers } from '../handlers';
import { CompletedNote, InlineLink } from '../styles';

/**
 * Per-kind card behavior, one entry per kind that needs any (OLIO-2700).
 *
 * A kind absent from here is not special: it gets the generic body and no slideout. Adding a kind
 * means adding one entry, not editing a branch in the chrome, the row map, and a predicate.
 */
// What every renderer gets. Handlers are passed whole rather than one callback at a time: every
// kind wants a different subset, and threading them individually meant editing the chrome for
// each kind migrated.
interface ActionContext {
  action: WorkflowAction;
  workflow: Workflow;
  handlers: ActionHandlers;
}

// What a body renderer gets. No slideout state: a body renders inside the row and has no business
// knowing whether a sibling panel is open.
export interface ActionCardContext extends ActionContext {
  // Set by the facility-decision panel, whose rows render their own result.
  hideResult: boolean;
  // The generic completed surface, already null unless this step is completed.
  genericResult: ReactNode;
  // The generic option buttons and waiting copy, already null unless they apply.
  genericActions: ReactNode;
}

// A slideout renders as a sibling of the row, so it gets the open/close pair and none of the
// body's composition pieces.
export interface SlideoutContext extends ActionContext {
  slideoutOpen: boolean;
  onCloseSlideout: () => void;
}

export interface ActionCardEntry {
  // Whether this instance opens a slideout. A predicate rather than a flag because it depends on
  // the instance: a review surface only exists once there is a result to review.
  hasSlideout?: (action: WorkflowAction) => boolean;
  // Rendered as a SIBLING of the row, never inside it — the row is a button when it has a
  // slideout, and the slideout's own controls must not nest inside that button.
  slideout?: (ctx: SlideoutContext) => ReactNode;
  // This kind's entire body. Free-form on purpose: the kinds are genuinely irregular, and a
  // schema of named slots grew a slot every time one of them did not fit. Compose
  // ctx.genericResult / ctx.genericActions to keep the generic surfaces, or leave them out to
  // replace them. Omit `body` altogether and the kind gets both, which is most kinds.
  body?: (ctx: ActionCardContext) => ReactNode;
}

const completed = (action: WorkflowAction) => action.isCompleted;

// The values the resolve step couldn't place, recorded on the action while it waits.
//
// camelCase because that is what arrives: OliveBranch deep-transforms response keys, so the API's
// `raw_value` reaches us as `rawValue` (config/application.rb excludes only admin/attrs and the
// import configurations). Reading the snake_case names silently yields undefined — the card
// rendered an empty quote where the referral's own wording should be.
//
// Read defensively besides: this is API-shaped JSON, not a typed value, and an action that hasn't
// run yet carries none of it.
//
// `options` is deliberately not read, even though the API still sends it. It is the choice list as
// it stood when the step first asked, and the card fetches the current one instead — see
// ConfirmReferralSource.
function outstandingSources(action: WorkflowAction): OutstandingSource[] {
  const result = action.outputFor('attr_resolution_result') as
    | { unresolved?: { valueName?: string; attrId: string; attrName: string; rawValue?: string | null }[] }
    | undefined;

  return (result?.unresolved ?? []).map((entry) => ({
    // Defaulted rather than required: a question dispatched before OLIO-3302 carries no
    // `valueName`, and referral intake declares exactly one value to ask about either way.
    valueName: entry.valueName ?? VALUE_NAMES.REFERRAL_SOURCE,
    attrId: entry.attrId,
    attrName: entry.attrName,
    // Null when the referral named no source at all: one falsy shape for the card to branch on for
    // its second state. Narrowed rather than cast, because this is jsonb the API only checks for
    // blankness — a non-string reaching the quoted wording throws inside render and takes the whole
    // category card down rather than this one row. Same defense as the completed surface below.
    rawValue: typeof entry.rawValue === 'string' ? entry.rawValue : null,
  }));
}

export const ACTION_CARDS: Record<string, ActionCardEntry> = {
  DenialCheck: {
    hasSlideout: completed,
    slideout: ({ action, workflow, slideoutOpen, onCloseSlideout }) => (
      <DenialCheckReview
        action={action as DenialCheckAction}
        workflow={workflow}
        open={slideoutOpen}
        onClose={onCloseSlideout}
      />
    ),
    // In a multi-action category the row itself is the slideout trigger; this text-only summary is
    // the body for a lone single-action category. No genericResult: the slideout IS the surface.
    body: ({ action, genericActions }) => (
      <>
        {action.isCompleted && <DenialCheckSummary action={action as DenialCheckAction} />}
        {genericActions}
      </>
    ),
  },
  SexOffenderCheck: {
    hasSlideout: completed,
    slideout: ({ action, workflow, slideoutOpen, onCloseSlideout, handlers }) => (
      <SexOffenderCheckReview
        action={action as SexOffenderCheckAction}
        workflow={workflow}
        open={slideoutOpen}
        onClose={onCloseSlideout}
        onSaveProgress={handlers.onSaveProgress}
        isSavingProgress={handlers.isSavingProgress}
      />
    ),
    body: ({ action, genericActions }) => (
      <>
        {action.isCompleted && <SexOffenderCheckSummary action={action as SexOffenderCheckAction} />}
        {genericActions}
      </>
    ),
  },
  // Adjudicated in its own slideout, which MUTATES — each Dismiss is a save_progress write. Same
  // shape as SexOffenderCheck's: the row is the trigger, and the summary is the body.
  //
  // Completed only, results or not: the slideout's empty state is where a reviewer confirms the
  // check actually RAN rather than inferring it from a green row, and a check whose matches have
  // all been dismissed has to reopen so a dismissal can be undone. Nothing opens while errored —
  // a failed run has no result, and the row carries the failure and its Try again instead.
  HighCostMeds: {
    hasSlideout: completed,
    slideout: ({ action, workflow, slideoutOpen, onCloseSlideout, handlers }) => (
      <HighCostMedsReview
        action={action as HighCostMedsAction}
        workflow={workflow}
        open={slideoutOpen}
        onClose={onCloseSlideout}
        onSaveProgress={handlers.onSaveProgress}
        isSavingProgress={handlers.isSavingProgress}
      />
    ),
    // Also rendered while running, which has its own "still verifying" copy. Not when errored:
    // that state is the row's error treatment and the generic errored note, with no summary.
    body: ({ action, workflow, genericActions }) => (
      <>
        {(action.isCompleted || action.isRunning) && (
          <HighCostMedsSummary action={action as HighCostMedsAction} workflow={workflow} />
        )}
        {genericActions}
      </>
    ),
  },
  // Both of these complete with a packet/readout rather than a recorded value, so actionConfig
  // gives them no resultValueName and the generic CompletedResult renders nothing for them. That
  // is why these can own the completed body outright instead of rendering alongside it.
  ProcessAttachments: {
    // The note owns both faces of this step's completed body — a successful parse and a failed one
    // — plus the retry the failed face needs. See ProcessAttachmentsNote for why it reads the
    // PRESENTED status rather than `isCompleted`.
    body: ({ action, workflow, handlers: { onEdit, onInvoke, isInvoking }, genericActions }) => (
      <>
        {action.isCompleted && (
          <ProcessAttachmentsNote
            action={action}
            workflow={workflow}
            onEdit={onEdit}
            onInvoke={onInvoke}
            isInvoking={isInvoking}
          />
        )}
        {genericActions}
      </>
    ),
  },
  ExtractAttributes: {
    body: ({ action, workflow, handlers: { onEdit }, genericActions }) => (
      <>
        {action.isCompleted && (
          <>
            <CompletedNote>
              Review extracted patient demographics below.{' '}
              {onEdit && (
                <InlineLink type='button' onClick={() => onEdit('demographics')} data-testid='edit-demographics'>
                  Edit demographics
                </InlineLink>
              )}
            </CompletedNote>
            {/* `onEdit` is handed down only when the viewer can edit a referral that is still open and
                not locked (InboundReferralPage), which is exactly who may answer a highlighted field. */}
            <ExtractionResults action={action} workflow={workflow} canResolve={onEdit !== undefined} />
          </>
        )}
        {genericActions}
      </>
    ),
  },
  // The outcome is always recordable (OLIO-2611), so its panel stands in for the generic options
  // and waiting copy in both pending and action_required, and its own result replaces the generic
  // one. hideResult is honored because the facility-decision rows render their own result.
  AcceptReferral: {
    body: ({ action, workflow, handlers, hideResult }) => (
      <>
        {/* onUpdateValues is load-bearing, not decorative: ReferralOutcomeResult gates Change
            selected facility on it, so omitting it silently removes the only way to move a decided
            referral to another building before the EHR sync (OLIO-2846). onAmend is the same for
            the per-row Change to Selected / Not Selected menus (OLIO-2973) — without it the
            completed card offers no way to change the recorded outcome. */}
        {action.isCompleted && !hideResult && (
          <ReferralOutcomeResult
            action={action}
            workflow={workflow}
            onAmend={handlers.onAmend}
            isCompleting={handlers.isCompleting}
            onUpdateValues={handlers.onUpdateValues}
          />
        )}
        {(action.isPending || action.isActionRequired) && (
          <ReferralOutcomePanel
            action={action}
            workflow={workflow}
            onComplete={handlers.onComplete}
            isCompleting={handlers.isCompleting}
          />
        )}
      </>
    ),
  },
  // The remaining kinds add a panel to the generic surfaces rather than replacing them, which is
  // why they compose genericResult and genericActions around their own piece. The ordering is the
  // one they had inline: result, then the kind's panel, then the actions.
  // Two states, not one. It still parks unseen whenever the source already matches one of the
  // client's names (actionConfig's renderOnlyWhenHumanNeeded), so there is no pending surface to
  // write. What is new is the completed one: answering completes the step now (OLIO-3302), and a
  // completed row rendering nothing would read as though the question had vanished.
  ResolveAttrValues: {
    body: ({ action, workflow, handlers: { onComplete, isCompleting } }) => (
      <>
        {action.isCompleted && <ConfirmedReferralSource workflow={workflow} />}
        {action.isActionRequired && (
          <ConfirmReferralSource
            action={action}
            workflow={workflow}
            outstanding={outstandingSources(action)}
            onComplete={onComplete}
            isCompleting={isCompleting}
          />
        )}
      </>
    ),
  },
  DuplicatePatientSearch: {
    body: ({ action, workflow, handlers, genericResult, genericActions }) => (
      <>
        {genericResult}
        {action.isActionRequired && (
          <DuplicatePatientReview
            action={action}
            workflow={workflow}
            onComplete={handlers.onComplete}
            isCompleting={handlers.isCompleting}
          />
        )}
        {action.isCompleted && <DuplicateLinkNote action={action} />}
        {genericActions}
      </>
    ),
  },
  // Landed on master after this registry did (OLIO-2846). Owns every state it renders in — running,
  // completed, errored and action_required, which is its ready-to-send state — so it is not gated on
  // one of them here. It returns null while `pending`, which under design 010 means the referral is
  // undecided, and the definition's own description renders instead via genericActions.
  SyncPatientToEhr: {
    body: ({ action, workflow, handlers, genericResult, genericActions }) => (
      <>
        {genericResult}
        <SyncPatientBody
          action={action as SyncPatientToEhrAction}
          workflow={workflow}
          onDispatch={handlers.onDispatch}
          onResend={handlers.onResend}
          onSendToExistingRecord={handlers.onSendToExistingRecord}
          isDispatching={handlers.isDispatching}
        />
        {genericActions}
      </>
    ),
  },
};

export function actionCardFor(action: WorkflowAction): ActionCardEntry {
  return ACTION_CARDS[action.kind] ?? {};
}

export function hasSlideoutFor(action: WorkflowAction): boolean {
  return actionCardFor(action).hasSlideout?.(action) ?? false;
}
